import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper.js";
import NewEmail from "../Inputs/NewEmail.jsx";
import NewPhone from "../Inputs/NewPhone.jsx";
import NewSelect from "../Inputs/NewSelect";
import NewText from "../Inputs/NewText";
import NewTitle from "../Inputs/NewTitle.jsx";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow";

// Slide 8: General Contractor/Operator Information
const BillingInformation = ({
  setSlideStatus,
  slideState,
  formStateLoaded,
}) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide6,
    slide10,
    setSlide10,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    titles,
    companies,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
    setPageNumber,
  } = useContext(FormContext);

  const { client, clientId, projectId } = useContext(DashboardContext);

  useEffect(() => {
    if (slide10.billingCompany === "") return;

    if (
      slide10.billingCompany === client.name &&
      !slide10.billingAdd &&
      !slide10.billingCity &&
      !slide10.billingState &&
      !slide10.billingZip &&
      !slide10.billingPhone &&
      !slide10.billingFax
    ) {
      let newState = {
        ...slide10,
        billingCompany: client.name,
        billingAdd: client.address,
        billingCity: client.city,
        billingState: client.state,
        billingZip: client.zip,
        billingPhone: client.phone,
        billingFax: client.fax,
      };

      setSlide10(newState);

      slide10Mutation.mutate(newState);
    }
  }, [slide10.billingCompany]);

  useEffect(() => {
    if (
      !slide10.billingCompany &&
      !slide10.billingAdd &&
      !slide10.billingCity &&
      !slide10.billingState &&
      !slide10.billingZip &&
      !slide10.billingPhone &&
      !slide10.billingFax
    ) {
      if (slide6.sameAsOwner === true) {
        let newState = {
          ...slide10,
          billingCompany: slide6.gcName,
          billingAdd: slide6.gcAdd,
          billingCity: slide6.gcCity,
          billingState: slide6.gcState,
          billingZip: slide6.gcZip,
          billingPhone: slide6.gcPhone,
          billingFax: slide6.gcFax,
        };

        setSlide10(newState);

        slide10Mutation.mutate(newState);
      }
    }
  }, [slide6.sameAsOwner, slide10.billingCompany]);

  // ^ On Mount, the page should scroll to the top
  useEffect(() => {
    if (client && client.shortName === "Greystone") {
      setPageNumber(11);
    }
    scrollToFirstRequiredField(slide10);
  }, []);

  // : MAIN QUERY LOGIC :

  const slide10Mutation = useSetSlide(
    clientId,
    projectId,
    10,
    setIsLoading,
    setSlideStatus,
  );

  // : INVALID INPUTS LOGIC :

  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "10",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <h2 className="question-section">Billing Company Information</h2>
          <p className="important-text">
            Please provide the following information about the company who will
            be billed for this project
          </p>
          <ResponsiveRow type={"center"}>
            <NewText
              idProp="billingCompany"
              label="Company's Name"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingCompany}
              debouncedSave={slide10Mutation}
              type="company"
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? companies : []}
              req={
                slide10.billingCompany === "" ||
                slide10.billingCompany.length < 3
              }
              disabledInput={disabledInputCheck(
                "billingCompany",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("billingCompany", disabledInputs) === false
                  ? getInputMessage("billingCompany", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingCompany",
                invalidInputsData,
                "10",
              )}
            />
            <NewPhone
              idProp="billingPhone"
              label="Office Phone #"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingPhone}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={!isValidPhoneNumber(slide10.billingPhone, false)}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              disabledInput={disabledInputCheck("billingPhone", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingPhone", disabledInputs) === false
                  ? getInputMessage("billingPhone", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingPhone",
                invalidInputsData,
                "10",
              )}
            />
            <NewText
              idProp="billingFax"
              label="Fax Number"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingFax}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              type="fax"
              req={slide10.billingFax === ""}
              disabledInput={disabledInputCheck("billingFax", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingFax", disabledInputs) === false
                  ? getInputMessage("billingFax", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingFax",
                invalidInputsData,
                "10",
              )}
            />
          </ResponsiveRow>
          <ResponsiveRow>
            <NewText
              idProp="billingAdd"
              label="Office Address"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingAdd}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={slide10.billingAdd === "" || slide10.billingAdd.length < 6}
              disabledInput={disabledInputCheck("billingAdd", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingAdd", disabledInputs) === false
                  ? getInputMessage("billingAdd", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingAdd",
                invalidInputsData,
                "10",
              )}
              type={"address"}
            />
            <NewText
              idProp="billingCity"
              label="City"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingCity}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={slide10.billingCity === "" || slide10.billingCity.length < 3}
              disabledInput={disabledInputCheck("billingCity", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingCity", disabledInputs) === false
                  ? getInputMessage("billingCity", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingCity",
                invalidInputsData,
                "10",
              )}
              type={"city"}
            />
            <NewSelect
              idProp="billingState"
              label="State"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingState}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={slide10.billingState === ""}
              disabledInput={disabledInputCheck("billingState", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingState", disabledInputs) === false
                  ? getInputMessage("billingState", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingState",
                invalidInputsData,
                "10",
              )}
              type="state"
            />
            <NewText
              idProp="billingZip"
              label="Zip"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingZip}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={slide10.billingZip === "" || slide10.billingZip.length !== 5}
              disabledInput={disabledInputCheck("billingZip", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("billingZip", disabledInputs) === false
                  ? getInputMessage("billingZip", invalidInputsData, "10")
                  : ""
              }
              invalidData={getInvalidData(
                "billingZip",
                invalidInputsData,
                "10",
              )}
              type={"zipCode"}
            />
          </ResponsiveRow>
          <h2 className="question-section">Contact Information</h2>
          <p className="important-text">
            Please provide the following information for the main billing
            contact
          </p>
          <ResponsiveRow>
            <NewText
              idProp="billingContactName"
              label="First & Last Name"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingContactName}
              debouncedSave={slide10Mutation}
              type="name"
              setIsLoading={setIsLoading}
              req={!isValidName(slide10.billingContactName, false)}
              autoData={predictiveTextFetched ? names : []}
              disabledInput={disabledInputCheck(
                "billingContactName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("billingContactName", disabledInputs) ===
                false
                  ? getInputMessage(
                      "billingContactName",
                      invalidInputsData,
                      "10",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "billingContactName",
                invalidInputsData,
                "10",
              )}
            />
            <NewTitle
              idProp="billingContactTitle"
              label="Title"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingContactTitle}
              debouncedSave={slide10Mutation}
              type="title"
              setIsLoading={setIsLoading}
              req={
                slide10.billingContactTitle === "" ||
                slide10.billingContactTitle.length < 3
              }
              autoData={predictiveTextFetched ? titles : []}
              disabledInput={disabledInputCheck(
                "billingContactTitle",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("billingContactTitle", disabledInputs) ===
                false
                  ? getInputMessage(
                      "billingContactTitle",
                      invalidInputsData,
                      "10",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "billingContactTitle",
                invalidInputsData,
                "10",
              )}
            />
            <NewPhone
              idProp="billingContactPhone"
              label="Phone Number"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingContactPhone}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={!isValidPhoneNumber(slide10.billingContactPhone, false)}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              disabledInput={disabledInputCheck(
                "billingContactPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("billingContactPhone", disabledInputs) ===
                false
                  ? getInputMessage(
                      "billingContactPhone",
                      invalidInputsData,
                      "10",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "billingContactPhone",
                invalidInputsData,
                "10",
              )}
            />
            <NewEmail
              idProp="billingContactEmail"
              label="Email Address"
              state={slide10}
              setState={setSlide10}
              val={slide10.billingContactEmail}
              debouncedSave={slide10Mutation}
              setIsLoading={setIsLoading}
              req={!isValidEmail(slide10.billingContactEmail, false)}
              autoData={predictiveTextFetched ? emails : []}
              disabledInput={disabledInputCheck(
                "billingContactEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("billingContactEmail", disabledInputs) ===
                false
                  ? getInputMessage(
                      "billingContactEmail",
                      invalidInputsData,
                      "10",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "billingContactEmail",
                invalidInputsData,
                "10",
              )}
            />
          </ResponsiveRow>
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default BillingInformation;
